import type React from 'react'
import { forwardRef } from 'react'
import { Link as ReactRouterLink } from 'react-router'
import { gatsbyOnLinkClick, gatsbyOnMouseEnter } from './lib/gatsbyHelpers'
import { useWhichFramework } from './useWhichFramework'

type Props = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  readonly to: string
  readonly 'data-testid'?: string
  readonly state?: Record<string, unknown>
}

/**
 * Link that works with both Gatsby and Remix.
 *
 * Feel free to add props as needed, just make sure they work for both Gatsby and Remix.
 */
export const Link = forwardRef(
  (
    {
      children,
      className,
      onClick,
      onMouseEnter,
      state,
      target,
      to,
      ...rest
    }: Props,
    ref: React.ForwardedRef<HTMLAnchorElement>
  ) => {
    const framework = useWhichFramework()
    const testId = rest['data-testid']

    return framework === 'gatsby' ? (
      <a
        className={className}
        data-component="Link"
        data-testid={testId ?? 'gatsby-link'}
        href={to}
        onClick={gatsbyOnLinkClick(to, { state, target, onClick })}
        onMouseEnter={gatsbyOnMouseEnter(to, onMouseEnter)}
        ref={ref}
        target={target}
        {...rest}
      >
        {children}
      </a>
    ) : (
      <ReactRouterLink
        className={className}
        data-component="Link"
        data-testid={testId ?? 'remix-link'}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        ref={ref}
        state={state}
        target={target}
        to={to}
        {...rest}
      >
        {children}
      </ReactRouterLink>
    )
  }
)
